<template>
  <ekitabu-chip
    color="formatChip"
    :outlined="!selected"
    :text-color="selected ? 'white' : 'formatChip'"
    :ripple="!selected"
    :class="[
      'format-chip',
      { 'format-chip--selected': selected, 'format-chip--current': entry.isCurrent },
    ]"
    role="radio"
    :aria-checked="String(selected)"
    :aria-current="entry.isCurrent ? 'true' : null"
    tabindex="0"
    @click="onSelect"
    @keydown.enter.prevent="onSelect"
    @keydown.space.prevent="onSelect"
  >
    <ekitabu-icon
      left
      :size="iconSize"
      class="format-chip__icon"
    >
      {{ entry.icon }}
    </ekitabu-icon>
    <span class="format-chip__label">{{ entry.label }}</span>
    <span
      class="format-chip__divider"
      aria-hidden="true"
    >·</span>
    <span class="format-chip__price">
      <price
        v-if="hasPrice"
        :book-price="Number(entry.book.price)"
        :base-currency="entry.book.currency._id"
        :add-tax="!entry.book.vatIncluded"
        :should-round="true"
        :skip-base-rounding="entry.book.vatIncluded || false"
      />
      <span v-else>{{ $t("shop.free") }}</span>
    </span>
  </ekitabu-chip>
</template>

<script>
import Price from "@/components/Price";

const ICON_SIZE_PX = 18;

export default {
  name: "FormatChip",
  components: { Price },
  props: {
    entry: {
      type: Object,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { iconSize: ICON_SIZE_PX };
  },
  computed: {
    hasPrice() {
      const price = Number(this.entry?.book?.price);
      return Number.isFinite(price) && price > 0 && this.entry?.book?.currency?._id;
    },
  },
  methods: {
    onSelect() {
      if (this.selected) return;
      this.$emit("select");
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/styles/variables.scss";

// ─── Component-local design tokens ────────────────────────────────────────
$chip-height: 40px;
$chip-padding-x: 16px;
$chip-radius: map-get($rounded, 'pill');
$chip-border-width: 1.5px;
$chip-gap-internal: 8px;

$chip-accent: $book-red;
$chip-accent-deep: $book-red-dark;
$chip-fill-selected: linear-gradient(135deg, $chip-accent-deep 0%, $chip-accent 100%);

$chip-shadow-selected:
  0 1px 0 rgba(white, 0.18) inset,
  0 4px 14px rgba($chip-accent-deep, 0.28),
  0 1px 3px rgba(black, 0.08);
$chip-shadow-hover:
  0 1px 2px rgba(black, 0.04),
  0 6px 18px rgba($chip-accent, 0.18);
$chip-shadow-rest:
  0 1px 2px rgba(black, 0.03);

$chip-divider-opacity: 0.45;
$chip-label-weight: 600;
$chip-label-tracking: 0.01em;
$chip-price-weight: 600;
$chip-hover-translate: -1px;
$chip-transition: 160ms $book-ease-out;
$chip-icon-shift: 1px;

// ─── Chip ────────────────────────────────────────────────────────────────
.format-chip.v-chip {
  height: $chip-height;
  padding: 0 $chip-padding-x;
  border-radius: $chip-radius;
  border-width: $chip-border-width;
  font-weight: $chip-label-weight;
  letter-spacing: $chip-label-tracking;
  cursor: pointer;
  transition:
    transform $chip-transition,
    box-shadow $chip-transition,
    background $chip-transition,
    border-color $chip-transition;
  box-shadow: $chip-shadow-rest;

  &:focus-visible {
    outline: 2px solid $chip-accent;
    outline-offset: 3px;
  }
}

// Selected: filled with brand gradient + soft glow.
.format-chip--selected.v-chip {
  cursor: default;
  background: $chip-fill-selected !important;
  border-color: transparent !important;
  box-shadow: $chip-shadow-selected;

  &:hover {
    transform: none;
    box-shadow: $chip-shadow-selected;
  }
}

// Unselected hover: lift + subtle accent-tinted shadow.
.format-chip:not(.format-chip--selected).v-chip:hover {
  transform: translateY($chip-hover-translate);
  border-color: $chip-accent;
  box-shadow: $chip-shadow-hover;

  .format-chip__icon {
    transform: translateX(-$chip-icon-shift);
  }
}

.format-chip__label {
  margin-right: $chip-gap-internal;
}

.format-chip__divider {
  margin: 0 $chip-gap-internal 0 0;
  opacity: $chip-divider-opacity;
}

.format-chip__icon {
  transition: transform $chip-transition;
}

.format-chip__price {
  display: inline-flex;
  align-items: center;
  font-weight: $chip-price-weight;
}

// `Price.vue` uses `.gradient-text` with !important on background-clip — for the
// selected (filled) variant we want plain white text instead of the brand gradient
// so it reads clearly against the colored chip background.
.format-chip--selected ::v-deep .gradient-text {
  background: none !important;
  -webkit-background-clip: border-box !important;
  background-clip: border-box !important;
  -webkit-text-fill-color: white !important;
  color: white !important;
}
</style>
